import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactMarkdownWithGFMPlugin } from "@/utils";
import { getTutorialsForPath } from "@/guides/tutorialsByRoute";
import "./guideDrawer.css";

const GuideDrawer = ({ isOpen, pathname, onClose }) => {
  const { t, i18n } = useTranslation();
  const tutorials = useMemo(() => getTutorialsForPath(pathname), [pathname]);
  const [selectedTutorialId, setSelectedTutorialId] = useState(null);
  const [markdownContent, setMarkdownContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Always reset to list view when route changes.
    setSelectedTutorialId(null);
    setMarkdownContent("");
  }, [tutorials]);

  useEffect(() => {
    // Opening the drawer should start from the tutorial list.
    if (isOpen) {
      setSelectedTutorialId(null);
      setMarkdownContent("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedTutorialId) {
      setMarkdownContent("");
      return;
    }

    const selectedTutorial = tutorials.find((item) => item.id === selectedTutorialId);
    if (!selectedTutorial) {
      setMarkdownContent("");
      return;
    }

    const controller = new AbortController();
    const loadMarkdown = async () => {
      setIsLoading(true);
      try {
        const lang = i18n.language || "en";
        const localizedPath = lang.startsWith("ja")
          ? selectedTutorial.markdownPath.replace(/^\/guides\//, "/guides/ja/")
          : selectedTutorial.markdownPath;

        let response = await fetch(localizedPath, {
          signal: controller.signal,
        });

        // Fallback to default English markdown if localized file fails to load
        if (!response.ok && localizedPath !== selectedTutorial.markdownPath) {
          response = await fetch(selectedTutorial.markdownPath, {
            signal: controller.signal,
          });
        }

        if (!response.ok) {
          throw new Error("Failed to load tutorial.");
        }

        const text = await response.text();
        if (!controller.signal.aborted) {
          setMarkdownContent(text);
        }
      } catch (error) {
        if (error.name !== "AbortError" && !controller.signal.aborted) {
          setMarkdownContent(
            t(
              "guideDrawer.unavailable",
              "# Tutorial unavailable\n\nUnable to load this tutorial right now.",
            ),
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadMarkdown();

    return () => {
      controller.abort();
    };
  }, [selectedTutorialId, tutorials, t, i18n.language]);

  useEffect(() => {
    const onEsc = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [isOpen, onClose]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;

    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalOverflow;
    }

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const selectedTutorial =
    tutorials.find((item) => item.id === selectedTutorialId) || null;

  const handleBackToList = () => {
    setSelectedTutorialId(null);
    setMarkdownContent("");
  };

  const showDetailsView = Boolean(selectedTutorial);

  return (
    <>
      <div
        className={`guide-drawer-overlay ${isOpen ? "open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
        role="presentation"
        tabIndex={-1}
      />

      <aside
        className={`guide-drawer ${isOpen ? "open" : ""}`}
        aria-hidden={!isOpen}
        inert={!isOpen ? true : undefined}
        aria-label={t("guideDrawer.pageTutorials", "Page tutorials")}
      >
        <div className="guide-drawer-header">
          {showDetailsView ? (
            <button className="btn btn-sm guide-drawer-back" onClick={handleBackToList}>
              <i className="bi bi-arrow-left" aria-hidden="true"></i>
              <span className="ms-2">{t("guideDrawer.back", "Back")}</span>
            </button>
          ) : (
            <h5 className="mb-0">{t("guideDrawer.title", "Page Guides")}</h5>
          )}
          <button className="btn btn-sm guide-drawer-close" onClick={onClose}>
            <i className="bi bi-x-lg" aria-hidden="true"></i>
            <span className="visually-hidden">
              {t("guideDrawer.close", "Close guide drawer")}
            </span>
          </button>
        </div>

        <div className="guide-drawer-body single-column">
          {!showDetailsView ? (
            <div className="guide-drawer-list guide-drawer-list-full">
              <h6 className="guide-drawer-section-title">
                {t("guideDrawer.sectionTitle", "Tutorials for this page")}
              </h6>
              {tutorials.length === 0 ? (
                <p className="guide-drawer-empty">
                  {t(
                    "guideDrawer.empty",
                    "No tutorial is available for this page yet.",
                  )}
                </p>
              ) : (
                tutorials.map((tutorial) => (
                  <button
                    key={tutorial.id}
                    className="guide-drawer-item"
                    onClick={() => setSelectedTutorialId(tutorial.id)}
                  >
                    {tutorial.titleKey ? t(tutorial.titleKey, tutorial.title) : tutorial.title}
                  </button>
                ))
              )}
            </div>
          ) : (
            <div className="guide-drawer-content guide-drawer-content-full">
              {isLoading ? (
                <div className="guide-drawer-loading">
                  {t("guideDrawer.loading", "Loading tutorial...")}
                </div>
              ) : (
                <ReactMarkdownWithGFMPlugin>{markdownContent}</ReactMarkdownWithGFMPlugin>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default GuideDrawer;
